import React, { useState, useEffect, useRef } from 'react';
import { Box, Tabs, Button, Modal, SpaceBetween } from '@amzn/awsui-components-console';
import JsonGrid from '@redheadphone/react-json-grid';
import ReactMarkdown from 'react-markdown';
import { cleanMermaidContent } from '../../utils/cleanMermaidContent';
import { sanitizeSvg } from '../../utils/sanitizeSvg';

// Custom Mermaid component with better error handling
const MermaidRenderer: React.FC<{ content: string }> = ({ content }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [svg, setSvg] = useState<string>('');
    const [error, setError] = useState<string>('');

    useEffect(() => {
        const renderMermaid = async () => {
            try {
                // Dynamically import mermaid
                const mermaid = (await import('mermaid')).default;
                
                // Initialize mermaid
                mermaid.initialize({
                    startOnLoad: false,
                    theme: 'default',
                    securityLevel: 'strict',
                    flowchart: {
                        useMaxWidth: true,
                        htmlLabels: true,
                        curve: 'basis'
                    }
                });

                // Generate unique ID
                const id = `mermaid-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
                
                // Sanitize content (handles single-line S3 content, special chars in labels)
                const sanitized = cleanMermaidContent(content);
                
                // Render the diagram
                const { svg: renderedSvg } = await mermaid.render(id, sanitized);
                setSvg(renderedSvg);
                setError('');
            } catch (err: any) {
                console.error('Error rendering mermaid diagram:', err);
                setError(err.message || 'Failed to render diagram');
                setSvg('');
            }
        };

        if (content) {
            renderMermaid();
        }
    }, [content]);

    if (error) {
        return (
            <Box color="text-status-error" padding="m">
                <div>Error rendering diagram: {error}</div>
                <pre style={{ fontSize: '12px', marginTop: '10px', whiteSpace: 'pre-wrap' }}>{content}</pre>
            </Box>
        );
    }

    if (!svg) {
        return <Box textAlign="center" padding="l">Loading diagram...</Box>;
    }

    return (
        <div 
            ref={containerRef}
            dangerouslySetInnerHTML={{ __html: sanitizeSvg(svg) }}
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
        />
    );
};

interface JsonDataViewerProps {
    data: any;
    mermaidDiagram?: string;
    aiSummary?: string;
    defaultView?: 'grid' | 'json' | 'mermaid' | 'summary';
    style?: React.CSSProperties;
    collapsed?: boolean | number;
    displayDataTypes?: boolean;
    displayObjectSize?: boolean;
    enableClipboard?: boolean;
    hideToolbar?: boolean;
}

const JsonDataViewer: React.FC<JsonDataViewerProps> = ({
    data,
    mermaidDiagram,
    aiSummary,
    defaultView = 'grid',
    style = {},
    collapsed = 2,
    displayDataTypes = false,
    displayObjectSize = false,
    enableClipboard = true,
    hideToolbar = false
}) => {
    const [activeTabId, setActiveTabId] = useState<string>(defaultView);
    const [JsonView, setJsonView] = useState<any>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Dynamically import JsonView component
    useEffect(() => {
        if (activeTabId === 'json' && !JsonView) {
            import('@uiw/react-json-view').then((module) => {
                setJsonView(() => module.default);
            });
        }
    }, [activeTabId, JsonView]);

    if (!data && !mermaidDiagram && !aiSummary) {
        return (
            <Box textAlign="center" color="text-status-inactive" padding="m">
                No data to display
            </Box>
        );
    }

    // Build tabs based on available data
    const tabs = [];
    
    // JSON Grid and JSON Raw tabs commented out for now
    // if (data) {
    //     tabs.push({
    //         id: 'grid',
    //         label: 'JSON Grid',
    //         content: (
    //             <div 
    //                 style={{
    //                     ...style,
    //                     minHeight: '200px',
    //                     maxHeight: style.maxHeight || '600px',
    //                     overflow: 'auto',
    //                     borderRadius: '4px',
    //                     padding: '8px'
    //                 }}
    //             >
    //                 <JsonGrid 
    //                     data={data}
    //                     theme="dracula"
    //                     defaultExpandDepth={typeof collapsed === 'number' ? collapsed : (collapsed ? 0 : 2)}
    //                 />
    //             </div>
    //         )
    //     });

    //     tabs.push({
    //         id: 'json',
    //         label: 'JSON Raw',
    //         content: (
    //             <div 
    //                 style={{
    //                     ...style,
    //                     minHeight: '200px',
    //                     maxHeight: style.maxHeight || '600px',
    //                     overflow: 'auto',
    //                     backgroundColor: '#1e1e1e',
    //                     borderRadius: '4px',
    //                     padding: '12px'
    //                 }}
    //             >
    //                 {JsonView ? (
    //                     <JsonView
    //                         value={data}
    //                         collapsed={collapsed}
    //                         displayDataTypes={displayDataTypes}
    //                         displayObjectSize={displayObjectSize}
    //                         enableClipboard={enableClipboard}
    //                         style={{
    //                             '--w-rjv-font-family': 'Monaco, Menlo, monospace',
    //                             '--w-rjv-color': '#9cdcfe',
    //                             '--w-rjv-background-color': '#1e1e1e',
    //                             '--w-rjv-line-color': '#3a3a3a',
    //                             '--w-rjv-arrow-color': '#9cdcfe',
    //                             '--w-rjv-key-string': '#9cdcfe',
    //                             '--w-rjv-key-number': '#b5cea8',
    //                             '--w-rjv-key-null': '#569cd6',
    //                             '--w-rjv-key-undefined': '#569cd6',
    //                             '--w-rjv-key-boolean': '#569cd6',
    //                             '--w-rjv-brackets-color': '#ffd700',
    //                             '--w-rjv-quotes-color': '#ce9178',
    //                             '--w-rjv-info-color': '#9cdcfe',
    //                             fontSize: '13px'
    //                         } as any}
    //                     />
    //                 ) : (
    //                     <Box textAlign="center" padding="l">
    //                         Loading JSON view...
    //                     </Box>
    //                 )
    //             }
    //             </div>
    //         )
    //     });
    // }

    if (aiSummary) {
        tabs.push({
            id: 'summary',
            label: 'AI Summary',
            content: (
                <div 
                    style={{
                        ...style,
                        minHeight: '200px',
                        maxHeight: style.maxHeight || '600px',
                        overflow: 'auto',
                        backgroundColor: '#fafbfc',
                        borderRadius: '4px',
                        padding: '16px 24px'
                    }}
                    className="ai-summary-content"
                >
                    <ReactMarkdown
                        components={{
                            h1: ({ children }) => <h1 style={{ fontSize: '1.5em', fontWeight: 600, marginBottom: '12px', color: '#16191f', borderBottom: '1px solid #e9ebed', paddingBottom: '8px' }}>{children}</h1>,
                            h2: ({ children }) => <h2 style={{ fontSize: '1.3em', fontWeight: 600, marginBottom: '10px', marginTop: '16px', color: '#16191f' }}>{children}</h2>,
                            h3: ({ children }) => <h3 style={{ fontSize: '1.1em', fontWeight: 600, marginBottom: '8px', marginTop: '12px', color: '#16191f' }}>{children}</h3>,
                            p: ({ children }) => <p style={{ marginBottom: '12px', lineHeight: '1.6', color: '#414d5c' }}>{children}</p>,
                            ul: ({ children }) => <ul style={{ marginBottom: '12px', paddingLeft: '24px', color: '#414d5c' }}>{children}</ul>,
                            ol: ({ children }) => <ol style={{ marginBottom: '12px', paddingLeft: '24px', color: '#414d5c' }}>{children}</ol>,
                            li: ({ children }) => <li style={{ marginBottom: '6px', lineHeight: '1.5' }}>{children}</li>,
                            code: ({ children, className }) => {
                                const isInline = !className;
                                return isInline ? (
                                    <code style={{ 
                                        backgroundColor: '#e9ebed', 
                                        padding: '2px 6px', 
                                        borderRadius: '4px', 
                                        fontSize: '0.9em',
                                        fontFamily: 'Monaco, Menlo, monospace',
                                        color: '#0972d3'
                                    }}>{children}</code>
                                ) : (
                                    <code style={{ 
                                        display: 'block',
                                        backgroundColor: '#1e1e1e', 
                                        padding: '12px', 
                                        borderRadius: '6px', 
                                        fontSize: '0.9em',
                                        fontFamily: 'Monaco, Menlo, monospace',
                                        color: '#9cdcfe',
                                        overflow: 'auto'
                                    }}>{children}</code>
                                );
                            },
                            pre: ({ children }) => <pre style={{ marginBottom: '12px' }}>{children}</pre>,
                            blockquote: ({ children }) => (
                                <blockquote style={{ 
                                    borderLeft: '4px solid #0972d3', 
                                    paddingLeft: '16px', 
                                    marginLeft: '0',
                                    marginBottom: '12px',
                                    color: '#5f6b7a',
                                    fontStyle: 'italic'
                                }}>{children}</blockquote>
                            ),
                            strong: ({ children }) => <strong style={{ fontWeight: 600, color: '#16191f' }}>{children}</strong>,
                            em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{children}</em>,
                            hr: () => <hr style={{ border: 'none', borderTop: '1px solid #e9ebed', margin: '16px 0' }} />,
                            table: ({ children }) => (
                                <div style={{ overflowX: 'auto', marginBottom: '12px' }}>
                                    <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '0.95em' }}>{children}</table>
                                </div>
                            ),
                            th: ({ children }) => <th style={{ border: '1px solid #e9ebed', padding: '8px 12px', backgroundColor: '#f2f3f3', fontWeight: 600, textAlign: 'left' }}>{children}</th>,
                            td: ({ children }) => <td style={{ border: '1px solid #e9ebed', padding: '8px 12px' }}>{children}</td>
                        }}
                    >
                        {aiSummary}
                    </ReactMarkdown>
                </div>
            )
        });
    }

    if (mermaidDiagram) {
        tabs.push({
            id: 'mermaid',
            label: 'Mermaid Diagram',
            content: (
                <SpaceBetween size="xs">
                    <Box float="right">
                        <Button
                            iconName="expand"
                            variant="icon"
                            onClick={() => setIsFullscreen(true)}
                            ariaLabel="View fullscreen"
                        />
                    </Box>
                    <div 
                        style={{
                            ...style,
                            minHeight: '200px',
                            maxHeight: style.maxHeight || '600px',
                            overflow: 'auto',
                            backgroundColor: '#ffffff',
                            borderRadius: '4px',
                            padding: '12px',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'flex-start'
                        }}
                    >
                        <MermaidRenderer content={mermaidDiagram} />
                    </div>
                </SpaceBetween>
            )
        });
    }

    // If no tabs available (shouldn't happen), show error
    if (tabs.length === 0) {
        return (
            <Box textAlign="center" color="text-status-inactive" padding="m">
                No data to display
            </Box>
        );
    }

    return (
        <>
            <Box>
                {!hideToolbar ? (
                    <Tabs
                        tabs={tabs}
                        activeTabId={activeTabId}
                        onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                    />
                ) : (
                    // If toolbar is hidden, just show the content of the default view
                    tabs.find(tab => tab.id === activeTabId)?.content || tabs[0].content
                )}
            </Box>
            
            {/* Fullscreen Modal for Mermaid Diagram */}
            {mermaidDiagram && (
                <Modal
                    visible={isFullscreen}
                    onDismiss={() => setIsFullscreen(false)}
                    closeAriaLabel="Close fullscreen view"
                    size="max"
                    header="Mermaid Diagram - Fullscreen View"
                >
                    <div 
                        style={{
                            width: '100%',
                            height: '70vh',
                            overflow: 'auto',
                            backgroundColor: '#ffffff',
                            borderRadius: '4px',
                            padding: '20px',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'flex-start'
                        }}
                    >
                        <div style={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                            <MermaidRenderer content={mermaidDiagram} />
                        </div>
                    </div>
                </Modal>
            )}
        </>
    );
};

export default JsonDataViewer;
